import { RequestContext } from "asosteam";
import { parse } from "cookie";
import type { AppProps, AppContext } from "next/app";
import App, { AppInitialProps } from "next/app";
import {
  getNavigation,
  type NavigationResponse,
} from "@repo/apis/browse-navigation-v2";
import { BrowseSearchV2 } from "@repo/apis/browse-search-v2";
import { SitecorePageProps } from "@repo/sitecore-lib/page-props";
import {
  setAuthoringShopHeaders,
  getRequestContext,
} from "@repo/sitecore-lib/store-presentation";
import { PageLoadAnalytics } from "@/components/analytics/page-load-analytics";
import { Footer } from "@/components/footer/footer";
import { Nav } from "@/components/nav";
import { SecureScriptContainerClient } from "@/components/secure-script-container/secure-script-container-client";
import { shouldShowHeroHeader } from "@/components/sitecore-components/layouts/utils";
import BrandLogo from "@/components/topshop-landing/brand-logo";
import { useShouldShowOverlay } from "@/components/topshop-landing/hooks";
import { relativePro } from "@/theme/fonts";
import { SpinnerIcon } from "@/theme/icons/spinner";
import { configurePageRouterServerRequest } from "@/utils/request-adapters/page-router-server-request";
import { Client } from "../app/client";
import "@/theme/topshop-1/index.css";
import "@/theme/topshop-2/index.css";

export type HeadersType = {
  headers: {
    isEditing: boolean;
    requestContext: RequestContext;
    navigation: NavigationResponse;
    trendingCategoryProducts: BrowseSearchV2 | null;
  };
  requestContext: RequestContext;
};

export type AppPageProps = SitecorePageProps & HeadersType;

function TopshopApp({
  Component,
  pageProps,
}: AppProps<AppPageProps>): JSX.Element {
  const { showLogoOverlay, isOverlayReady, activeFloor } =
    useShouldShowOverlay();

  const { navigation, isEditing } = pageProps.headers;

  if (!pageProps.headers || (!isEditing && !isOverlayReady)) {
    return <SpinnerIcon label="Loading..." />;
  }

  const isContentNavTransparent = pageProps.layoutData
    ? shouldShowHeroHeader(pageProps.layoutData)
    : false;

  const { requestContext } = pageProps;
  const { countryData, keyStoreDataversion, shop, versionManifest, platform } =
    requestContext;

  return (
    <main className={relativePro.className}>
      <Client
        countryData={countryData}
        keyStoreDataversion={keyStoreDataversion}
        shop={shop}
        versionManifest={versionManifest}
      >
        <Nav
          initialNavigation={navigation}
          isContentNavTransparent={isContentNavTransparent}
          requestContext={requestContext}
        />
        {showLogoOverlay && !isEditing && (
          <BrandLogo activeFloor={activeFloor!} />
        )}
        <Component {...pageProps} activeFloor={activeFloor!} />
        <Footer />
        <PageLoadAnalytics
          browseCurrency={countryData.defaultCurrency}
          browseCountry={countryData.countryCode}
          language={countryData.defaultLanguage}
          channel="default"
          platform={platform}
        />
        <SecureScriptContainerClient />
      </Client>
    </main>
  );
}

TopshopApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps & { pageProps: AppPageProps }> => {
  const appProps = await App.getInitialProps(appContext);

  const req = appContext.ctx.req;

  if (!req) {
    throw new Error("Request object is undefined in getInitialProps");
  }

  let isEditing = false;

  if (req?.headers?.cookie) {
    const parsedCookies = parse(req.headers.cookie || "");
    isEditing = parsedCookies.isEditing === "true";
  }

  setAuthoringShopHeaders(req);

  const requestContext = await getRequestContext(req);

  let navigation: NavigationResponse | null;

  try {
    const serverRequest = configurePageRouterServerRequest({
      req: req,
    });

    if (isEditing) {
      navigation = null;
    } else {
      navigation = await getNavigation({
        requestInstance: serverRequest,
        requestContext,
      });
    }
  } catch (error) {
    console.error("Failed to fetch navigation:", error);
    navigation = null;
    throw error;
  }

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      headers: {
        isEditing,
        navigation,
      },
      requestContext,
    },
  };
};

export default TopshopApp;
